export enum RESILIENCE_SCORE_LEVEL_2 {
  SOIL_HEALTH = "soilHealth",
  BIODIVERSITY = "biodiversity",
  WATER_CAPACITY = "waterCapacity",
}
export enum RESILIENCE_SCORE_LEVEL_3 {
  CROP_ROTATION_SCORE = "cropRotationScore",
  TILLAGE_SCORE = "tillageScore",
  SOIL_COVER_SCORE = "soilCoverScore",
}

export type ResilienceScoreViewModel = {
  overallScore: number;
  maxScore: number;
  riskLevel: "low" | "medium" | "high";
  soilHealthScore: number;
  soilHealthTrend: ResilienceTrendDirection;
  soilHealthChangePct: number | null;
  waterManagementScore: number;
  waterManagementTrend: ResilienceTrendDirection;
  waterManagementChangePct: number | null;
  biodiversityScore: number;
  biodiversityTrend: ResilienceTrendDirection;
  biodiversityChangePct: number | null;
};

export type ResilienceTrendDirection = "up" | "down" | "flat" | "none";

export type FarmResilienceFieldMetricBreakdown = {
  score: number | null;
  soilCoverScore?: number | null;
  cropRotationScore?: number | null;
  tillageScore?: number | null;
};

export type FarmResilienceFieldYear = {
  score: number | null;
  biodiversity: FarmResilienceFieldMetricBreakdown;
  soilHealth: FarmResilienceFieldMetricBreakdown;
  waterCapacity: FarmResilienceFieldMetricBreakdown;
};

export type FarmResilienceFieldEntry = {
  fieldId: string;
  // dynamic year keys like "2023", "2024", ...
  [year: string]: string | FarmResilienceFieldYear;
};

export type FarmResilienceApiResponse = FarmResilienceFieldEntry[];

const MAX_SCORE_DEFAULT = 10;
/** Clamp score to valid range (0-10). */
export const clampScore = (value: number | null | undefined, fallback = 0) => {
  if (value == null || Number.isNaN(value)) return fallback;
  if (value < 0) return 0;
  if (value > MAX_SCORE_DEFAULT) return MAX_SCORE_DEFAULT;
  return value;
};

/** Derive risk level based on score. */
export const deriveRiskLevel = (score: number | null | undefined): "low" | "medium" | "high" => {
  const value = clampScore(score);
  if (value > 6) return "low";
  if (value < 3) return "high";
  return "medium";
};

/** Map trend direction based on delta. */
export const mapTrendDirectionFromDelta = (delta: number | null): ResilienceTrendDirection => {
  if (delta == null) return "none";
  if (delta > 0.001) return "up";
  if (delta < -0.001) return "down";
  return "flat";
};

/** Calculate percentage change between two numbers. with considering that base is not 0. and delta is not null. */
export const calcPct = (delta: number | null, base: number | null | undefined) =>
  delta !== null && base !== null && base !== undefined && base !== 0 ? (delta / base) * 100 : null;

/** Get year data from field. */
const getYearDataFromField = (field: FarmResilienceFieldEntry, year: number): FarmResilienceFieldYear | null => {
  const raw = (field as Record<string, unknown>)[String(year)];
  if (!raw || typeof raw !== "object") return null;
  return raw as FarmResilienceFieldYear;
};

type AggregatedMetrics = {
  overall: number;
  soilHealth: number;
  waterCapacity: number;
  biodiversity: number;
};

/** Calculate average of an array of numbers. */
const average = (values: number[]): number => {
  if (!values.length) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
};

/**
 * Build aggregated metrics for a single year.
 */
const buildAggregatedMetricsForYear = (fields: FarmResilienceFieldEntry[], year: number): AggregatedMetrics | null => {
  const overallScoresForYear: number[] = [];
  const soilHealthScoresForYear: number[] = [];
  const biodiversityScoresForYear: number[] = [];
  const waterCapacityScoresForYear: number[] = [];

  fields.forEach((field) => {
    const yearData = getYearDataFromField(field, year);
    if (!yearData || yearData.score == null) return;

    overallScoresForYear.push(clampScore(yearData.score));
    soilHealthScoresForYear.push(clampScore(yearData.soilHealth?.score));
    biodiversityScoresForYear.push(clampScore(yearData.biodiversity?.score));
    waterCapacityScoresForYear.push(clampScore(yearData.waterCapacity?.score));
  });

  if (!overallScoresForYear.length) return null;

  return {
    overall: average(overallScoresForYear),
    soilHealth: average(soilHealthScoresForYear),
    biodiversity: average(biodiversityScoresForYear),
    waterCapacity: average(waterCapacityScoresForYear),
  };
};

export const buildResilienceScoreViewModel = (
  response: FarmResilienceApiResponse | null | undefined,
  selectedYear: number
): ResilienceScoreViewModel | null => {
  if (!response || !response?.length) return null;

  const fields = response as FarmResilienceFieldEntry[];

  const currentAgg = buildAggregatedMetricsForYear(fields, selectedYear);
  if (!currentAgg) return null;

  const previousAgg = buildAggregatedMetricsForYear(fields, selectedYear - 1);

  const riskLevel = deriveRiskLevel(currentAgg.overall);

  const soilDelta = previousAgg ? currentAgg.soilHealth - previousAgg.soilHealth : null;
  const waterDelta = previousAgg ? currentAgg.waterCapacity - previousAgg.waterCapacity : null;
  const biodiversityDelta = previousAgg ? currentAgg.biodiversity - previousAgg.biodiversity : null;
  // Yearly change in percentage
  const soilPct = calcPct(soilDelta, previousAgg?.soilHealth);
  const waterPct = calcPct(waterDelta, previousAgg?.waterCapacity);
  const biodiversityPct = calcPct(biodiversityDelta, previousAgg?.biodiversity);

  return {
    overallScore: clampScore(currentAgg.overall),
    maxScore: MAX_SCORE_DEFAULT,
    riskLevel,
    soilHealthScore: clampScore(currentAgg.soilHealth),
    soilHealthTrend: mapTrendDirectionFromDelta(soilDelta),
    soilHealthChangePct: soilPct,
    waterManagementScore: clampScore(currentAgg.waterCapacity),
    waterManagementTrend: mapTrendDirectionFromDelta(waterDelta),
    waterManagementChangePct: waterPct,
    biodiversityScore: clampScore(currentAgg.biodiversity),
    biodiversityTrend: mapTrendDirectionFromDelta(biodiversityDelta),
    biodiversityChangePct: biodiversityPct,
  };
};
